import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { MailboxType, type IAccount } from '../models/account.models';
import { ComposeType, type IComposeParams, type IComposePanel, type IDraftPlaceholder, type IComposePanelAttachment } from '../models/compose.models';
import { type IDraft } from '../models/draft.models';

import { ConversationService } from './conversation.service';
import { AppState } from 'src/app/core/app.state';
import { selectAccounts } from '../store/smart-inbox.selectors';
import * as smartInboxActions from '../store/smart-inbox.actions';
import { ComposeOverlayHostComponent } from '../components/inbox-view/compose/compose-overlay-host/compose-overlay-host.component';

const HOST_DIALOG_CONFIG = {
  hasBackdrop: false,
  panelClass: 'compose-overlay-pane',
  maxWidth: 'none',
  width: '100vw',
  height: '100vh',
  position: { top: '0', left: '0' },
};

const BASE_Z = 1000;

/**
 * Manages the lifecycle of compose/reply/forward panels. Opens a single
 * MatDialog overlay host and tracks panel state (position, size, z-index,
 * minimized) via a BehaviorSubject. Fetches sender accounts from the store.
 */
@Injectable({ providedIn: 'root' })
export class ComposeService {
  private panels$ = new BehaviorSubject<IComposePanel[]>([]);
  readonly panels = this.panels$.asObservable();

  private nextId = 0;
  private nextZ = BASE_Z;
  private hostDialogRef: MatDialogRef<ComposeOverlayHostComponent> | null = null;
  private accounts: IAccount[] = [];

  constructor(
    private dialog: MatDialog,
    private conversationService: ConversationService,
    private store: Store<AppState>,
  ) {
    this.store.select(selectAccounts).subscribe((accounts) => {
      this.accounts = accounts;
    });
  }

  openCompose(params: IComposeParams = {}): void {
    const type = params.type ?? ComposeType.COMPOSE;
    const id = `${type}-${++this.nextId}`;
    const zIndex = ++this.nextZ;
    const senderAccounts = this.accounts.filter(
      (account) =>
        account.mailbox_type === MailboxType.PERSONAL || account.mailbox_type === MailboxType.TEAM,
    );
    const from = params.from ?? senderAccounts[0] ?? null;
    const isReplyType = type === ComposeType.REPLY || type === ComposeType.REPLY_ALL;
    let body = params.body ?? '';

    const fromAccount = from?.id ? this.accounts.find((a) => a.id === from.id) : null;
    if (fromAccount) {
      const isForward = type === ComposeType.FORWARD;
      const sigBody = (isReplyType || isForward)
        ? fromAccount.default_signature_reply_forward?.body
        : fromAccount.default_signature_new?.body;
      if (sigBody) {
        const signatureHtml = `<div class="volta-signature">${sigBody}</div>`;
        body = isForward && body
          ? `${body}${signatureHtml}`
          : body ? `${signatureHtml}${body}` : signatureHtml;
      }
    }

    const panel: IComposePanel = {
      id,
      type,
      from,
      to: params.to ?? '',
      subject: params.subject ?? '',
      cc: params.cc ?? '',
      bcc: params.bcc ?? '',
      body,
      zIndex,
      minimized: false,
      draftId: params.draftId ?? null,
      ...(params.reply_to_conversation != null && { reply_to_conversation: params.reply_to_conversation }),
      ...(params.reply_to_message != null && { reply_to_message: params.reply_to_message }),
      ...(params.autoAiDraft != null && { autoAiDraft: params.autoAiDraft }),
      attachments: params.attachments ?? [],
    };
    this.panels$.next([...this.panels$.value, panel]);

    if (!this.hostDialogRef) {
      this.hostDialogRef = this.dialog.open(ComposeOverlayHostComponent, HOST_DIALOG_CONFIG);
      this.hostDialogRef.afterClosed().subscribe(() => {
        this.hostDialogRef = null;
        this.panels$.next([]);
      });
    }
  }

  closeCompose(id: string): void {
    const next = this.panels$.value.filter((p) => p.id !== id);
    this.panels$.next(next);
    if (next.length === 0 && this.hostDialogRef) {
      this.hostDialogRef.close();
      this.hostDialogRef = null;
    }
  }

  sendCompose(id: string): Observable<any> {
    const panel = this.panels$.value.find((p) => p.id === id);
    if (!panel || !panel.from) return of(null);
    return this.conversationService.compose({ ...panel });
  }

  bringToFront(id: string): void {
    const list = this.panels$.value;
    const panel = list.find((p) => p.id === id);
    if (!panel) return;
    const newZ = ++this.nextZ;
    this.panels$.next(list.map((p) => (p.id === id ? { ...p, zIndex: newZ } : p)));
  }

  setSending(id: string, sending: boolean): void {
    this.panels$.next(
      this.panels$.value.map((p) => (p.id === id ? { ...p, sending } : p)),
    );
  }

  setMinimized(id: string, minimized: boolean): void {
    this.panels$.next(
      this.panels$.value.map((p) => (p.id === id ? { ...p, minimized } : p)),
    );
  }

  restoreAllMinimized(): void {
    const list = this.panels$.value;
    if (!list.some((p) => p.minimized)) return;
    this.panels$.next(list.map((p) => ({ ...p, minimized: false })));
  }

  updatePanelPosition(id: string, positionX: number, positionY: number): void {
    this.panels$.next(
      this.panels$.value.map((p) =>
        p.id === id ? { ...p, positionX, positionY } : p,
      ),
    );
  }

  updatePanelSize(id: string, width: number, height: number): void {
    this.panels$.next(
      this.panels$.value.map((p) =>
        p.id === id ? { ...p, width, height } : p,
      ),
    );
  }

  updatePanelBody(id: string, body: string | null): void {
    if (!body) return;
    this.panels$.next(
      this.panels$.value.map((p) =>
        p.id === id ? { ...p, body } : p,
      ),
    );
  }

  updatePanelAiDraft(id: string, aiDraft: IComposePanel['aiDraft']): void {
    this.panels$.next(
      this.panels$.value.map((p) =>
        p.id === id ? { ...p, aiDraft } : p,
      ),
    );
  }

  addAttachments(id: string, toAdd: IComposePanelAttachment[]): void {
    if (!toAdd.length) return;
    this.panels$.next(
      this.panels$.value.map((p) => {
        if (p.id !== id) return p;
        const current = p.attachments ?? [];
        return { ...p, attachments: [...current, ...toAdd] };
      }),
    );
  }

  setAttachments(id: string, attachments: IComposePanelAttachment[]): void {
    this.panels$.next(
      this.panels$.value.map((p) =>
        p.id === id ? { ...p, attachments: [...attachments] } : p,
      ),
    );
  }

  getPanelById(id: string): IComposePanel | undefined {
    return this.panels$.value.find((p) => p.id === id);
  }

  getPanelByDraftId(draftId: number): IComposePanel | undefined {
    return this.panels$.value.find((p) => p.draftId === draftId);
  }

  /**
   * Opens an existing draft in a compose panel: focuses it if already open,
   * otherwise maps the draft into a new panel. Shared by the drafts list and
   * the workflow_send_reply_failed notification deep-link.
   */
  openFromDraft(draft: IDraft): void {
    const existing = this.getPanelByDraftId(draft.id);
    if (existing) {
      this.bringToFront(existing.id);
      this.setMinimized(existing.id, false);
      return;
    }

    this.openCompose({
      type: draft.type ?? ComposeType.COMPOSE,
      draftId: draft.id ?? null,
      to: (draft.to ?? []).join(', '),
      cc: (draft.cc ?? []).join(', '),
      subject: draft.subject || '',
      body: draft.body || '',
      attachments: (draft.attachments ?? []).map((a) => ({ id: a.id, name: a.name, size: a.file_size })),
    });
  }

  setDraftId(id: string, draftId: number): void {
    this.panels$.next(
      this.panels$.value.map((p) => (p.id === id ? { ...p, draftId,  } : p)),
    );
  }

  /**
   * Dispatches CreateDraft or UpdateDraft based on whether the panel
   * already has a draftId. The compose panel component listens for
   * success/error actions to manage the saving spinner.
   */
  saveDraft(panelId: string): void {
    const panel = this.panels$.value.find((p) => p.id === panelId);
    if (!panel) return;

    const snapshot = { ...panel };
    if (panel.draftId) {
      this.store.dispatch(smartInboxActions.UpdateDraft({ panel: snapshot, draftId: panel.draftId }));
    } else {
      this.store.dispatch(smartInboxActions.CreateDraft({ panel: snapshot }));
    }
  }

  deleteDraft(panelId: string): void {
    const panel = this.panels$.value.find((p) => p.id === panelId);
    if (!panel?.draftId) {
      this.closeCompose(panelId);
      return;
    }

    this.store.dispatch(smartInboxActions.DeleteDraft({ draftId: panel.draftId }));
    this.closeCompose(panelId);
  }
}
